import React, { useMemo, useState } from 'react';
import { X, Calendar as CalendarIcon, MapPin, Clock, List as ListIcon, ExternalLink, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-US', {
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return ''; }
};

const fmtFull = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const fmtDateKey = (iso) => {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDayLabel = (iso) =>
  new Date(iso).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

const CategoryDot = ({ category }) => {
  if (!category) return null;
  // Tiny hashed color from category string
  let h = 0;
  for (let i = 0; i < category.length; i++) h = (h * 31 + category.charCodeAt(i)) % 360;
  return (
    <span
      className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle"
      style={{ background: `hsl(${h}, 65%, 45%)` }}
    />
  );
};

const EventRow = ({ ev }) => {
  const cat = (ev.categories && ev.categories[0]) || ev.organizer || '';
  return (
    <a
      href={ev.url || '#'}
      target={ev.url ? '_blank' : undefined}
      rel="noreferrer"
      className="block px-4 py-3 hover:bg-slate-50 transition-colors group"
    >
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center justify-start pt-0.5 w-9 flex-shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-rose-500 font-semibold">
            {new Date(ev.start).toLocaleString('en-US', { month: 'short' })}
          </div>
          <div className="text-[18px] leading-none font-bold text-slate-900">
            {new Date(ev.start).getDate()}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-medium text-slate-900 leading-snug group-hover:text-slate-700">
            {ev.title}
          </div>
          {cat && (
            <div className="text-[11.5px] text-slate-500 mt-0.5">
              <CategoryDot category={cat} />
              {cat}
            </div>
          )}
          <div className="mt-1 flex items-center gap-1 text-[11.5px] text-slate-600">
            <Clock size={11} className="text-slate-400" />
            {fmtTime(ev.start)}
            {ev.location && (
              <>
                <span className="mx-1 text-slate-300">·</span>
                <MapPin size={11} className="text-rose-400" fill="currentColor" />
                <span className="truncate max-w-[180px]">{ev.location}</span>
              </>
            )}
          </div>
        </div>
        {ev.url && (
          <ExternalLink size={13} className="text-slate-300 group-hover:text-slate-600 mt-1 flex-shrink-0" />
        )}
      </div>
    </a>
  );
};

const ListView = ({ events }) => {
  // Group events by day
  const groups = useMemo(() => {
    const out = {};
    for (const e of events) {
      const key = fmtDateKey(e.start);
      if (!out[key]) out[key] = [];
      out[key].push(e);
    }
    return Object.entries(out).sort(([a], [b]) => a.localeCompare(b));
  }, [events]);

  if (!events.length) {
    return <div className="px-4 py-10 text-center text-[13px] text-slate-500">No upcoming events.</div>;
  }

  return (
    <div className="divide-y divide-slate-100">
      {groups.map(([key, list]) => (
        <div key={key}>
          <div className="sticky top-0 z-[1] bg-slate-50/95 backdrop-blur-sm border-b border-slate-100 px-4 py-1.5 text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
            {fmtDayLabel(list[0].start)}
            <span className="ml-1.5 text-slate-400 normal-case font-medium">
              · {list.length} event{list.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="divide-y divide-slate-100">
            {list.map((ev) => (<EventRow key={ev.id} ev={ev} />))}
          </div>
        </div>
      ))}
    </div>
  );
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

const CalendarView = ({ events }) => {
  const [cursor, setCursor] = useState(() => {
    const d = events.length ? new Date(events[0].start) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedKey, setSelectedKey] = useState(null);

  const byDay = useMemo(() => {
    const map = {};
    for (const e of events) {
      const k = fmtDateKey(e.start);
      (map[k] = map[k] || []).push(e);
    }
    return map;
  }, [events]);

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  // Grid: start from Sunday before monthStart
  const gridStart = new Date(monthStart);
  gridStart.setDate(monthStart.getDate() - monthStart.getDay());
  const gridEnd = new Date(monthEnd);
  gridEnd.setDate(monthEnd.getDate() + (6 - monthEnd.getDay()));

  const days = [];
  for (let d = new Date(gridStart); d <= gridEnd; d.setDate(d.getDate() + 1)) {
    days.push(new Date(d));
  }

  const todayKey = fmtDateKey(new Date());
  const monthLabel = cursor.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  const selectedEvents = selectedKey ? (byDay[selectedKey] || []) : [];

  return (
    <div>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
        <button
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
          className="p-1 rounded-md hover:bg-slate-100 text-slate-600"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="text-[13px] font-semibold text-slate-900">{monthLabel}</div>
        <button
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
          className="p-1 rounded-md hover:bg-slate-100 text-slate-600"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="px-3 pb-3">
        <div className="grid grid-cols-7 gap-0.5 mt-2 mb-1">
          {['S','M','T','W','T','F','S'].map((d, i) => (
            <div key={i} className="text-center text-[10px] text-slate-400 font-medium uppercase tracking-wide py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {days.map((d, i) => {
            const k = fmtDateKey(d);
            const inMonth = d.getMonth() === cursor.getMonth();
            const isToday = k === todayKey;
            const count = (byDay[k] || []).length;
            const isSelected = selectedKey === k;
            return (
              <button
                key={i}
                onClick={() => setSelectedKey(isSelected ? null : (count ? k : null))}
                disabled={!count}
                className={`aspect-square rounded-md text-[12px] font-medium relative flex flex-col items-center justify-center transition-colors ${
                  isSelected
                    ? 'bg-slate-900 text-white'
                    : count
                      ? 'bg-emerald-50 text-slate-900 hover:bg-emerald-100'
                      : inMonth
                        ? 'text-slate-700 hover:bg-slate-50'
                        : 'text-slate-300'
                } ${isToday && !isSelected ? 'ring-1 ring-rose-400' : ''}`}
              >
                <span>{d.getDate()}</span>
                {count > 0 && (
                  <span className={`text-[9px] mt-0.5 ${isSelected ? 'text-emerald-200' : 'text-emerald-700'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {selectedKey && (
        <div className="border-t border-slate-100">
          <div className="px-4 py-2 text-[11px] font-semibold text-slate-600 uppercase tracking-wide bg-slate-50">
            {fmtDayLabel(selectedKey + 'T12:00:00')}
            <span className="ml-1.5 text-slate-400 normal-case font-medium">
              · {selectedEvents.length} event{selectedEvents.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="divide-y divide-slate-100 max-h-[260px] overflow-y-auto">
            {selectedEvents.map((ev) => (<EventRow key={ev.id} ev={ev} />))}
          </div>
        </div>
      )}
    </div>
  );
};

const EventsPanel = ({ open, events, loading, onClose, onRefresh }) => {
  const [tab, setTab] = useState('list');

  if (!open) return null;

  return (
    <div className="absolute top-32 right-4 z-[950] w-[400px] max-h-[calc(100vh-220px)] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-right-2 duration-200 flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <CalendarIcon size={15} className="text-rose-500" />
          <h3 className="font-semibold text-slate-900 text-[14px]">Portland tech events</h3>
          {!loading && (
            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
              {events.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {onRefresh && (
            <button
              onClick={onRefresh}
              title="Refresh events"
              className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            </button>
          )}
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 px-3 pt-2 pb-2 border-b border-slate-100">
        <button
          onClick={() => setTab('list')}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium transition-colors ${
            tab === 'list' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <ListIcon size={12} /> List
        </button>
        <button
          onClick={() => setTab('calendar')}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium transition-colors ${
            tab === 'calendar' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <CalendarIcon size={12} /> Calendar
        </button>
        <div className="flex-1" />
        <a
          href="https://portland-tech.topher-timemachine.workers.dev/"
          target="_blank"
          rel="noreferrer"
          className="text-[11px] text-slate-400 hover:text-slate-700"
        >
          via portland.tech
        </a>
      </div>

      <div className="overflow-y-auto flex-1">
        {loading && !events.length ? (
          <div className="px-4 py-10 text-center text-[13px] text-slate-500">Loading events…</div>
        ) : tab === 'list' ? (
          <ListView events={events} />
        ) : (
          <CalendarView events={events} />
        )}
      </div>
    </div>
  );
};

export default EventsPanel;
